<script setup>
// 📦 KARGO KURTARMA v7 — İŞ ODAKLI YENİDEN TASARIM (Sevgi geri bildirimi 2026-08-31)
//
// Eski panel paketleri KARGO KONUMUNA göre 3 sekmede diziyordu (Şubede / Hareket
// Halinde / Geçmiş). Temsilci ise kendi İŞİNE göre çalışıyor: "aradım, sonucu
// girdim, kayıt hâlâ listede — ne zaman düşecek?" Yığın 22 kayıttı, arama/filtre
// yoktu, not alanı arka uçta vardı ama ekranda yoktu.
//
// Yeni model: tek liste + İŞ DURUMU filtreleri (sunucu tarafında).
//   📞 Aranacak · ⏳ Cevapsız · 🔁 Tükendi · 🚫 İstemiyor · ✅ Takipte · 🚛 Hareket
// Her kartta "şu an ne bekleniyor" cümlesi var — listeden ne zaman düşeceği artık
// tahmin işi değil. Her kartta temsilcinin kendi not alanı var.
//
// ⚠️ RENK DERSİ (v5): bu panelin Tailwind paleti green-600/yellow-600 gibi standart
// tonları ÜRETMİYOR — kritik renkler DOĞRUDAN inline hex ile verilir.
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';
import RescueCardDetail from 'dashboard/components-next/CargoRescue/RescueCardDetail.vue';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(false);
const error = ref('');
const items = ref([]);
const resolved = ref([]);
const stats = ref({});
const counts = ref({});
const agents = ref([]);
const busyId = ref(null);
const openId = ref(null);

const state = ref('aranacak'); // varsayılan: bugün yapılacak iş
const search = ref('');
const sort = ref('oncelik');
const agent = ref('');
const noteDrafts = ref({});

/* ---------------------------------------------------------------- durumlar */
const STATES = [
  {
    key: 'aranacak',
    label: '📞 Aranacak',
    hint: 'Bugün senin araman gerekenler.',
    color: '#b91c1c',
    bg: '#fee2e2',
  },
  {
    key: 'cevapsiz',
    label: '⏳ Cevapsız',
    hint: 'Ulaşılamadı, tekrar deneme günü henüz gelmedi.',
    color: '#92400e',
    bg: '#fef3c7',
  },
  {
    key: 'tukendi',
    label: '🔁 Tükendi',
    hint: '3 kez arandı, ulaşılamadı. İade kararı verilmeli.',
    color: '#6d28d9',
    bg: '#ede9fe',
  },
  {
    key: 'istemiyor',
    label: '🚫 İstemiyor',
    hint: 'Müşteri almayacak. İade süreci işleyecek.',
    color: '#b91c1c',
    bg: '#fee2e2',
  },
  {
    key: 'takipte',
    label: '✅ Takipte',
    hint: 'Müşteri alacağını söyledi. Sende iş yok, kargoyu bekliyoruz.',
    color: '#15803d',
    bg: '#dcfce7',
  },
  {
    key: 'harekette',
    label: '🚛 Hareket Halinde',
    hint: 'Paket şubede değil, yolda. Arama anlamsız.',
    color: '#1d4ed8',
    bg: '#dbeafe',
  },
  {
    key: 'gecmis',
    label: '🗂️ Geçmiş',
    hint: 'Son 30 günde kapanan kayıtlar.',
    color: '#374151',
    bg: '#e5e7eb',
  },
];

const SORTS = [
  { key: 'oncelik', label: 'Öncelik' },
  { key: 'tutar', label: 'Tutar (yüksek)' },
  { key: 'eski', label: 'En eski' },
  { key: 'yeni', label: 'En yeni' },
];

const RESULTS = [
  { key: 'alacak', label: '✅ Alacak', style: 'background:#16a34a;color:#fff' },
  {
    key: 'ulasilamadi',
    label: '📵 Ulaşılamadı',
    style: 'background:#d97706;color:#fff',
  },
  {
    key: 'istemiyor',
    label: '🚫 İstemiyor',
    style: 'background:#b91c1c;color:#fff',
  },
];

const activeState = computed(
  () => STATES.find(s => s.key === state.value) || STATES[0]
);
const isHistory = computed(() => state.value === 'gecmis');

/* -------------------------------------------------------------------- veri */
// silent: arka planda tazeleme — spinner göstermez, liste yerinde kalır.
// (Çip tıklamasından sonra tam ekran "Yükleniyor…" göstermek paneli donuk
// hissettiriyordu; kart zaten anında güncelleniyor, sayaçlar sessizce gelir.)
const load = async ({ silent = false } = {}) => {
  if (!silent) loading.value = true;
  error.value = '';
  try {
    const p = new URLSearchParams();
    if (!isHistory.value) p.set('state', state.value);
    if (search.value.trim()) p.set('q', search.value.trim());
    if (sort.value) p.set('sort', sort.value);
    if (agent.value) p.set('agent', agent.value);
    const data = await fetchMarketplaceJson(
      accountId,
      `cargo_rescues?${p.toString()}`
    );
    items.value = data.items || [];
    resolved.value = data.resolved || [];
    stats.value = data.stats || {};
    counts.value = data.counts || {};
    agents.value = data.agents || [];
  } catch (e) {
    error.value = 'Liste yüklenemedi. Bağlantıyı kontrol edip tekrar deneyin.';
  } finally {
    loading.value = false;
  }
};
onMounted(() => load());

let searchTimer = null;
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => load(), 350);
});
watch([state, sort, agent], () => load());

// Geçmiş listesi istemci tarafında aranır (sunucu 30 günü tek seferde verir).
const historyRows = computed(() => {
  const q = search.value.trim().toLocaleLowerCase('tr-TR');
  if (!q) return resolved.value;
  return resolved.value.filter(i =>
    `${i.customer_name} ${i.phone} ${i.order_number} ${i.tracking_no}`
      .toLocaleLowerCase('tr-TR')
      .includes(q)
  );
});

const rows = computed(() =>
  isHistory.value ? historyRows.value : items.value
);

/* ------------------------------------------------------------------ işlem */
const mark = async (item, payload) => {
  busyId.value = item.id;
  try {
    const updated = await patchMarketplaceJson(
      accountId,
      `cargo_rescues/${item.id}`,
      payload
    );
    Object.assign(item, updated); // kart ANINDA güncellenir
    load({ silent: true }); // sayaçlar/sıralama arka planda tazelenir (beklemeyiz)
  } catch (e) {
    error.value = 'Kaydedilemedi, tekrar deneyin.';
  } finally {
    busyId.value = null;
  }
};

const saveNote = async item => {
  const draft = noteDrafts.value[item.id];
  if (draft === undefined || draft === (item.call_note || '')) return;
  await mark(item, { call_note: draft });
  delete noteDrafts.value[item.id];
};

const noteValue = item =>
  noteDrafts.value[item.id] !== undefined
    ? noteDrafts.value[item.id]
    : item.call_note || '';

const setNote = (item, value) => {
  noteDrafts.value = { ...noteDrafts.value, [item.id]: value };
};

const noteDirty = item =>
  noteDrafts.value[item.id] !== undefined &&
  noteDrafts.value[item.id] !== (item.call_note || '');

const closeItem = async (item, resolution) => {
  const isim = item.customer_name || item.tracking_no;
  const soru =
    resolution === 'kurtarildi'
      ? `${isim} — paketi müşteri TESLİM ALDI olarak kapatılsın mı?`
      : `${isim} — iadesi DEPOYA GELDİ olarak kapatılsın mı?`;
  // eslint-disable-next-line no-alert
  if (!window.confirm(soru)) return;
  await mark(item, { resolution });
  await load({ silent: true }); // kayıt listeden düşer
};

const undoClose = async item => {
  await mark(item, { resolution: '' });
};

const toggleOpen = item => {
  openId.value = openId.value === item.id ? null : item.id;
};

/* --------------------------------------------------------------- gösterim */
const tl = v => (v || 0).toLocaleString('tr-TR');
const saat = t =>
  t
    ? new Date(t).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
const tarih = t =>
  t
    ? new Date(t).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'short',
      })
    : '';

const fmtPhone = p => {
  const d = String(p || '')
    .replace(/\D/g, '')
    .replace(/^90/, '')
    .replace(/^0+/, '');
  if (!/^5\d{9}$/.test(d)) return p || '—';
  return `0${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8)}`;
};

const stateStyle = key => {
  const s = STATES.find(x => x.key === key);
  return s ? `background:${s.bg};color:${s.color}` : '';
};
const stateLabel = key => STATES.find(x => x.key === key)?.label || key;

const unanswered24h = item =>
  item.notified_at &&
  !item.customer_reply &&
  Date.now() - new Date(item.notified_at).getTime() > 24 * 3600 * 1000;
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-5xl w-full mx-auto px-4 sm:px-5 py-6">
      <!-- ============================ BAŞLIK ============================ -->
      <h1 class="text-2xl sm:text-3xl font-bold text-n-slate-12">
        📦 Kargo Kurtarma
      </h1>
      <p class="text-n-slate-11 mt-1 mb-4">
        Teslim edilemeyip şubede bekleyen paketleri müşteriyi arayarak kurtarma
        listesi.
      </p>

      <!-- ============================ ÖZET ============================ -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-bold" style="color: #b91c1c">
            {{ counts.aranacak || 0 }}
          </div>
          <div class="text-sm text-n-slate-11">bugün aranacak</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-bold" style="color: #15803d">
            {{ counts.takipte || 0 }}
          </div>
          <div class="text-sm text-n-slate-11">takipte (işin bitti)</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-bold text-n-slate-12">
            {{ tl(stats.risk_tl) }} ₺
          </div>
          <div class="text-sm text-n-slate-11">risk altındaki tutar</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
          <div class="text-2xl font-bold" style="color: #15803d">
            {{ stats.hafta_kurtarilan || 0 }}
          </div>
          <div class="text-sm text-n-slate-11">bu hafta kurtarılan</div>
        </div>
      </div>

      <!-- ============================ FİLTRE ============================ -->
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 p-3 mb-3 flex flex-col gap-3"
      >
        <div class="flex flex-wrap gap-2">
          <button
            v-for="s in STATES"
            :key="s.key"
            class="px-3 py-2 rounded-lg text-sm font-bold border"
            :style="
              state === s.key
                ? `${stateStyle(s.key)};border-color:${s.color}`
                : 'background:transparent;border-color:#d1d5db'
            "
            :class="state === s.key ? '' : 'text-n-slate-11'"
            @click="state = s.key"
          >
            {{ s.label }}
            <span v-if="s.key !== 'gecmis'" class="ml-1 opacity-80">
              {{ counts[s.key] || 0 }}
            </span>
          </button>
        </div>

        <p class="text-sm text-n-slate-11 -mt-1">{{ activeState.hint }}</p>

        <div
          class="flex flex-wrap items-center gap-2 pt-2 border-t border-n-weak"
        >
          <input
            v-model="search"
            type="search"
            placeholder="İsim, telefon, sipariş no, not…"
            class="flex-1 min-w-[180px] rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
          />
          <select
            v-if="!isHistory"
            v-model="sort"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
          >
            <option v-for="s in SORTS" :key="s.key" :value="s.key">
              {{ s.label }}
            </option>
          </select>
          <select
            v-if="!isHistory && agents.length"
            v-model="agent"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
          >
            <option value="">Herkes</option>
            <option v-for="a in agents" :key="a" :value="a">{{ a }}</option>
          </select>
        </div>
      </div>

      <div
        v-if="error"
        class="rounded-lg px-3 py-2 mb-3 font-semibold"
        style="background: #fee2e2; color: #b91c1c"
      >
        {{ error }}
      </div>
      <div v-if="loading" class="text-n-slate-11 py-6 text-center">
        Yükleniyor…
      </div>

      <!-- ============================ LİSTE ============================ -->
      <template v-if="!loading">
        <p
          v-if="!rows.length"
          class="rounded-xl border border-dashed border-n-weak p-8 text-center text-n-slate-11"
        >
          <template v-if="search">
            “{{ search }}” için kayıt bulunamadı.
          </template>
          <template v-else-if="state === 'aranacak'">
            🎉 Bugün aranacak paket kalmadı.
          </template>
          <template v-else>Bu listede kayıt yok.</template>
        </p>

        <div
          v-for="item in rows"
          :key="item.id"
          class="rounded-xl border bg-n-solid-1 mb-2 overflow-hidden"
          :style="`border-color:${
            STATES.find(s => s.key === item.work_state)?.color || '#d1d5db'
          }33`"
        >
          <!-- başlık -->
          <button
            class="w-full text-left p-3 flex flex-wrap items-start justify-between gap-2"
            @click="toggleOpen(item)"
          >
            <div class="min-w-0">
              <div
                class="font-bold text-n-slate-12 flex flex-wrap items-center gap-1"
              >
                <span class="text-n-slate-10">{{
                  openId === item.id ? '▾' : '▸'
                }}</span>
                {{ item.customer_name || 'İsimsiz' }}
                <span
                  v-if="!isHistory"
                  class="text-xs font-bold px-2 py-0.5 rounded-full"
                  :style="stateStyle(item.work_state)"
                >
                  {{ stateLabel(item.work_state) }}
                </span>
                <span
                  v-if="item.repeat_count >= 2"
                  class="text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #fee2e2; color: #b91c1c"
                >
                  ⚠️ {{ item.repeat_count }}. kez almıyor
                </span>
                <span
                  v-if="item.customer_reply === 'almayacak'"
                  class="text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #fee2e2; color: #b91c1c"
                >
                  💬 “almayacağım” dedi
                </span>
                <span
                  v-else-if="item.customer_reply === 'alacak'"
                  class="text-xs font-bold px-2 py-0.5 rounded-full"
                  style="background: #dcfce7; color: #15803d"
                >
                  💬 “alacağım” dedi
                </span>
                <span
                  v-else-if="unanswered24h(item)"
                  class="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style="background: #e5e7eb; color: #4b5563"
                >
                  ✉️ mesaj cevapsız
                </span>
              </div>

              <div class="text-sm text-n-slate-11 mt-0.5">
                {{ tl(item.amount) }} ₺ ·
                {{ item.order_number || item.tracking_no }} · Şube:
                {{ item.dest_branch || item.branch || '—' }}
                <template v-if="!isHistory">
                  · {{ item.age_days }} gündür açık
                </template>
              </div>

              <div
                v-if="item.customer_reason"
                class="text-xs font-semibold mt-0.5"
                style="color: #b91c1c"
              >
                Müşteri nedeni: “{{ item.customer_reason }}”
              </div>
            </div>

            <div class="text-right shrink-0">
              <a
                v-if="!isHistory"
                :href="`tel:${item.phone}`"
                class="font-bold text-lg"
                style="color: #2563eb"
                @click.stop
              >
                📞 {{ fmtPhone(item.phone) }}
              </a>
              <div v-else class="text-sm font-bold">
                <span
                  v-if="item.resolution === 'kurtarildi'"
                  style="color: #15803d"
                  >🎉 KURTARILDI</span
                >
                <span
                  v-else-if="item.resolution === 'iade_dondu'"
                  style="color: #b91c1c"
                  >↩️ İADE DÖNDÜ</span
                >
                <span v-else class="text-n-slate-10">⏳ netleşiyor</span>
                <div class="text-xs font-normal text-n-slate-10">
                  {{ item.resolved_at }}
                </div>
              </div>
            </div>
          </button>

          <!-- ŞU AN NE BEKLENİYOR — "ne zaman düşecek" sorusunun cevabı -->
          <div
            v-if="!isHistory"
            class="mx-3 mb-2 rounded-lg px-3 py-2 text-sm font-semibold"
            :style="stateStyle(item.work_state)"
          >
            {{ item.waiting_for }}
          </div>

          <!-- geçmişte: kim ne yaptı izi -->
          <div v-else class="mx-3 mb-2 text-sm text-n-slate-11">
            <span v-if="item.resolved_by"
              >✋ {{ item.resolved_by }} elle kapattı</span
            >
            <span v-else>Sistem kargo kaydından kapattı</span>
            <span v-if="item.called_by">
              · {{ item.called_by }} aradı ({{ item.call_attempts }} deneme)
            </span>
            <button
              v-if="item.resolved_by"
              class="ml-2 underline"
              @click="undoClose(item)"
            >
              ↩︎ Geri Al
            </button>
          </div>
          <div
            v-if="isHistory && item.call_note"
            class="mx-3 mb-2 rounded-lg px-3 py-2 text-sm"
            style="background: #f3f4f6; color: #374151"
          >
            📝 {{ item.call_note }}
          </div>

          <!-- arama sonucu + kapatma -->
          <div
            v-if="!isHistory"
            class="flex flex-wrap items-center gap-2 px-3 pb-2"
          >
            <button
              v-for="r in RESULTS"
              :key="r.key"
              class="px-3 py-2 rounded-lg text-sm font-bold"
              :style="
                item.call_result === r.key
                  ? r.style
                  : 'background:#e5e7eb;color:#111'
              "
              :disabled="busyId === item.id"
              @click="
                mark(item, {
                  call_result: item.call_result === r.key ? '' : r.key,
                })
              "
            >
              {{ r.label }}
            </button>
            <span v-if="item.call_attempts" class="text-xs text-n-slate-10">
              {{ item.call_attempts }} deneme · son: {{ item.called_by }}
              {{ saat(item.last_called_at) }}
              <template v-if="item.next_attempt_on">
                · tekrar: {{ item.retry_label }}
              </template>
            </span>
          </div>

          <!-- TEMSİLCİ NOTU (Sevgi'nin isteği) -->
          <div v-if="!isHistory" class="px-3 pb-3">
            <label class="block text-xs font-bold text-n-slate-10 mb-1">
              📝 Notum
              <span v-if="item.note_by" class="font-normal">
                — son yazan {{ item.note_by }},
                {{ tarih(item.note_updated_at) }}
                {{ saat(item.note_updated_at) }}
              </span>
            </label>
            <div class="flex gap-2">
              <textarea
                :value="noteValue(item)"
                rows="2"
                placeholder="Müşteriyle konuşulanı buraya yaz: ne dedi, ne zaman alacak, ne yapılmalı…"
                class="flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
                @input="setNote(item, $event.target.value)"
                @blur="saveNote(item)"
              />
              <button
                v-if="noteDirty(item)"
                class="px-3 py-2 rounded-lg text-sm font-bold self-start"
                style="background: #2563eb; color: #fff"
                :disabled="busyId === item.id"
                @click="saveNote(item)"
              >
                Kaydet
              </button>
            </div>
          </div>

          <div v-if="!isHistory" class="flex flex-wrap gap-2 px-3 pb-3">
            <button
              class="px-3 py-2 rounded-lg text-sm font-bold"
              style="background: #dcfce7; color: #15803d"
              :disabled="busyId === item.id"
              @click="closeItem(item, 'kurtarildi')"
            >
              🎉 Teslim Aldı
            </button>
            <button
              class="px-3 py-2 rounded-lg text-sm font-bold"
              style="background: #ede9fe; color: #6d28d9"
              :disabled="busyId === item.id"
              @click="closeItem(item, 'iade_dondu')"
            >
              📦 İade Geldi
            </button>
          </div>

          <RescueCardDetail
            v-if="openId === item.id"
            :item="item"
            :account-id="accountId"
          />
        </div>
      </template>
    </div>
  </div>
</template>
